import { Component, EventEmitter, Input, Output } from '@angular/core';

export type TypographyElement =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'div'
  | 'label'
  | 'sup'
  | 'anchor';

export type TypographyVariant =
  | 'heading 2'
  | 'heading 1'
  | 'header'
  | 'skinnyHeader'
  | 'mobileHeader'
  | 'secondaryHeader'
  | 'body'
  | 'largeBody'
  | 'tag'
  | 'link'
  | 'smallBody'
  | 'extraSmall'
  | 'smallCaption';

export type TypographyColor =
  | 'textPrimary'
  | 'textSecondary'
  | 'primary'
  | 'secondary'
  | 'white';

@Component({
  selector: 'app-typography',
  templateUrl: './typography.component.html',
})
export class TypographyComponent {
  /**
   * Determines which html element (<h1>...<h6>, <p>, <span>) to use
   */
  @Input() component: TypographyElement = 'p';
  /**
   * Determines which variant to use
   */
  @Input() variant: TypographyVariant = 'body';
  /**
   * Determines the color of the text
   */
  @Input() color: TypographyColor = 'textPrimary';
  /**
   * Set this prop to true if you want some margin bottom by default
   */
  @Input() gutterBottom: boolean = true;
  /**
   * Use this prop to override classes.
   * Note: The styles regarding font-size, font-weight and line-height can't be overridden by this prop.
   */
  @Input() overrideClass: string = '';

  /**
   * Use this for label
   */
  @Input() for: string = '';

  /**
   * TODO: update the description
   */
  @Input() href: string = '';

  /**
   * Holds the name of classes that are used by the element.
   * Margin bottom of 8px (mb-2) is used as default
   */
  className: string = '';

  @Output() clickEvent = new EventEmitter();

  ngOnInit() {
    this.setClassName();
  }

  setClassName() {
    const baseClassNames = `text-${this.color} ${this.gutterBottom ? 'mb-1' : ''} ${this.overrideClass ? this.overrideClass : ''}`;
    switch (this.variant) {
      case 'heading 2':
        this.className = `${baseClassNames} text-[1.25rem] md:text-[1.5rem] lg:text-[1.875rem] 2xl:text-[2.25rem] font-bold leading-[2.875rem]"`;
        break;
      case 'heading 1':
        this.className = `${baseClassNames} text-[2.375rem] md:text-[3rem] lg:text-[3.35rem] 2xl:text-[4rem] font-bold leading-[4.625rem]"`;
        break;
      case 'header':
        this.className = `${baseClassNames} text-xl font-bold leading-6`;
        break;
      case 'skinnyHeader':
        this.className = `${baseClassNames} text-xl font-normal leading-6`;
        break;
      case 'mobileHeader':
        this.className = `${baseClassNames} text-base font-bold leading-6`;
        break;
      case 'secondaryHeader':
        this.className = `${baseClassNames} text-[.875rem] md:text-13 font-bold leading-6`;
        break;
      case 'body':
        this.className = `${baseClassNames} text-[.875rem] md:text-base font-normal leading-6`;
        break;
      case 'largeBody':
        this.className = `${baseClassNames} text-lg font-bold leading-[1rem]`;
        break;
      case 'tag':
        this.className = `${baseClassNames} text-base font-normal`;
        break;
      case 'link':
        this.className = `${baseClassNames} text-sm font-normal leading-6 cursor-pointer`;
        break;
      case 'smallBody':
        this.className = `${baseClassNames} text-sm leading-[1.4rem]`;
        break;
      case 'extraSmall':
        this.className = `${baseClassNames} text-xs font-normal leading-[1rem]`;
        break;
      case 'smallCaption':
        this.className = `${baseClassNames} text-[.625rem] font-normal leading-1`;
        break;
      default:
        this.className = '';
        break;
    }
  }

  handleClick() {
    this.href ? window.open(this.href, '_blank') : this.clickEvent.emit();
  }
}
